/**
 * Shared JSON generation utilities extracted from Preview.js.
 * Used by both Preview (for display/download) and App (for publish).
 */
import { buildAdditionalFieldsJSONPublished } from "./additionalFieldsJsonShape";
import { resolveSectionGoToForExport } from "./sectionGoTo";
import { buildSignaturesTemplate } from "./signatureUtils";

/** Canonical key order for published PDF rects (avoids alphabetical drift from merged React state). */
const PDF_LOCATION_PUBLISH_KEY_ORDER = ["page", "x", "y", "width", "height"];

/**
 * Category order must publish as a number, never a string ("20" -> 20).
 * The Form Settings text field yields a string, and older saved forms may
 * already hold one, so coerce at the serialization boundary.
 */
export const toCategoryOrder = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Section field order must publish as a whole number. State can hold a
 * non-int in several ways, so coerce at the serialization boundary:
 *   - float: overflow boxes carry `parentOrder + n/1000` (see formParser);
 *     that becomes a real field order the moment "Is Overflow" is cleared,
 *     and it survives every subsequent round-trip.
 *   - string: parseFormJSON preserves an imported `order` verbatim.
 *   - NaN: one non-numeric order poisons the `Math.max(...) + 1` that every
 *     add-field path uses, which JSON.stringify would emit as `null`.
 *   - undefined: dialog-created fields carry no order until assigned.
 * `fallback` is the field's 1-based position within its section.
 */
export const toFieldOrder = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : fallback;
};

function normalizePdfLocationRectForPublish(pl) {
  if (!pl || typeof pl !== "object" || Array.isArray(pl)) return pl;
  const out = {};
  for (const k of PDF_LOCATION_PUBLISH_KEY_ORDER) {
    if (Object.prototype.hasOwnProperty.call(pl, k)) out[k] = pl[k];
  }
  if (Object.prototype.hasOwnProperty.call(pl, "lines")) out.lines = pl.lines;
  for (const k of Object.keys(pl)) {
    if (PDF_LOCATION_PUBLISH_KEY_ORDER.includes(k) || k === "lines") continue;
    out[k] = pl[k];
  }
  return out;
}

/** `additionalFieldsJSON[]` entries use `pdfLocation` (always an array of rects). */
function normalizePdfLocationKeyOrderForPublish(pl) {
  if (pl == null) return pl;
  if (Array.isArray(pl)) return pl.map((r) => normalizePdfLocationRectForPublish(r));
  return normalizePdfLocationRectForPublish(pl);
}

function normalizeMetadataPdfLocationsForPublish(meta) {
  if (!meta || typeof meta !== "object") return meta;
  const next = { ...meta };
  if (next.pdfLocation != null && typeof next.pdfLocation === "object") {
    next.pdfLocation = Array.isArray(next.pdfLocation)
      ? next.pdfLocation.map((r) => normalizePdfLocationRectForPublish(r))
      : normalizePdfLocationRectForPublish(next.pdfLocation);
  }
  if (Array.isArray(next.overflow)) {
    next.overflow = next.overflow.map((entry) =>
      entry && typeof entry === "object" && entry.pdfLocation
        ? { ...entry, pdfLocation: normalizePdfLocationRectForPublish(entry.pdfLocation) }
        : entry,
    );
  }
  if (Array.isArray(next.options)) {
    next.options = next.options.map((opt) =>
      opt && typeof opt === "object" && opt.pdfLocation
        ? { ...opt, pdfLocation: normalizePdfLocationRectForPublish(opt.pdfLocation) }
        : opt,
    );
  }
  if (next.other && typeof next.other === "object") {
    const o = { ...next.other };
    for (const k of ["checkbox", "radio", "input"]) {
      const slot = o[k];
      if (slot && typeof slot === "object" && slot.pdfLocation) {
        o[k] = { ...slot, pdfLocation: normalizePdfLocationRectForPublish(slot.pdfLocation) };
      }
    }
    next.other = o;
  }
  return next;
}

/** Plain object that looks like one PDF rect (avoids matching arbitrary `{ page: number }`). */
function isPdfRectLikeObject(o) {
  if (!o || typeof o !== "object" || Array.isArray(o)) return false;
  if (typeof o.page !== "number") return false;
  const coordKeys = ["x", "y", "width", "height"];
  const coordCount = coordKeys.filter((k) => typeof o[k] === "number").length;
  return coordCount >= 2;
}

/**
 * Last-step publish pass: every nested rect gets canonical key order.
 * Catches paths that bypass buildPublishedFieldOutput (e.g. metadata.content, section metadata, raw merges).
 */
function deepNormalizePdfRectKeyOrder(value) {
  if (value == null) return value;
  if (Array.isArray(value)) {
    return value.map((v) => deepNormalizePdfRectKeyOrder(v));
  }
  if (typeof value !== "object") return value;
  if (isPdfRectLikeObject(value)) {
    return normalizePdfLocationRectForPublish(value);
  }
  const out = {};
  for (const k of Object.keys(value)) {
    out[k] = deepNormalizePdfRectKeyOrder(value[k]);
  }
  return out;
}

/**
 * One tab publishes as `pdfLocation`, several as `pdfLocations[]`.
 *
 * A signer may carry more than one signature or initials tab — the same person signing or
 * initialling in several places on the form. The published contract is XOR: exactly one of the two
 * keys is set, so a single-tab signer emits byte-identical output to before and only multi-tab
 * signers gain the array.
 *
 * @param {Array} locations - rects for one role+signer, in placement order
 * @returns {Object} `{}`, `{ pdfLocation }`, or `{ pdfLocations }`
 */
const publishTabLocations = (locations) => {
  const rects = (locations || []).filter(Boolean).map((l) => normalizePdfLocationRectForPublish(l));
  if (rects.length === 0) return {};
  if (rects.length === 1) return { pdfLocation: rects[0] };
  return { pdfLocations: rects };
};

const reorderDataExtraction = (dataExtraction) => {
  if (!dataExtraction) return dataExtraction;
  const result = {};
  for (const [key, value] of Object.entries(dataExtraction)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const { documentType, configName, configVersion, demoFiles, ...rest } = value;
      result[key] = {
        ...(documentType !== undefined && { documentType }),
        ...(configName !== undefined && { configName }),
        ...(configVersion !== undefined && { configVersion }),
        ...(demoFiles !== undefined && {
          demoFiles: demoFiles.map(({ demoFilename, demoExtractionId, ...fileRest }) => ({
            ...(demoFilename !== undefined && { demoFilename }),
            ...(demoExtractionId !== undefined && { demoExtractionId }),
            ...fileRest,
          })),
        }),
        ...rest,
      };
    } else {
      result[key] = value;
    }
  }
  return result;
};

const reorderValidation = (v) => {
  if (!v || typeof v !== "object") return v;
  const { min, max, minLength, maxLength, patterns, required, ...rest } = v;
  return {
    ...(min !== undefined && { min }),
    ...(max !== undefined && { max }),
    ...(minLength !== undefined && { minLength }),
    ...(maxLength !== undefined && { maxLength }),
    ...(patterns !== undefined && { patterns }),
    ...(required !== undefined && { required }),
    ...rest,
  };
};

const reorderContent = (content) => {
  if (!Array.isArray(content)) return content;
  return content.map((item) => {
    if (!item || typeof item !== "object") return item;
    const { attributeName, placeholder, preLabel, postLabel, value, validation, validationErrorMessage, width, customTemplateDoNotCopy, ...rest } = item;
    return {
      ...(attributeName !== undefined && { attributeName }),
      ...(placeholder !== undefined && { placeholder }),
      ...(preLabel !== undefined && { preLabel }),
      ...(postLabel !== undefined && { postLabel }),
      ...(value !== undefined && { value }),
      ...(validation !== undefined && { validation: reorderValidation(validation) }),
      ...(validationErrorMessage !== undefined && { validationErrorMessage }),
      ...(width !== undefined && { width }),
      ...rest,
      // Trails the rest for the same reason it does on the field itself.
      ...(customTemplateDoNotCopy !== undefined && { customTemplateDoNotCopy }),
    };
  });
};

/**
 * Canonical key order for `metadata.other.input`.
 *
 * Two shapes exist in the wild and their relative orders conflict, so neither
 * can be folded into the other:
 *   • slim  — the paired "Other" text input: attributeName first.
 *   • full  — `decisionWithOther` carries a COMPLETE field object here, whose
 *             keys follow the published field order (placeholder before
 *             attributeName). Detected by `order` / `type` being present.
 *
 * Without this the order is simply inherited from whatever the source happened
 * to carry, so the same template publishes differently depending on the path:
 * applying a local bundle keeps the authored order, while republishing from
 * Studio emits the DB's copy, which is alphabetical (a Go map / jsonb
 * round-trip does not preserve key order). Imposing the order here makes the
 * output identical either way.
 */
const OTHER_INPUT_KEY_ORDER_SLIM = [
  "attributeName",
  "placeholder",
  "validation",
  "validationErrorMessage",
  "value",
  "metadata",
  "pdfLocation",
];

const OTHER_INPUT_KEY_ORDER_FULL = [
  "order",
  "type",
  "constraint",
  "constraintId",
  "placeholder",
  "info",
  "hint",
  "label",
  "attributeName",
  "eventTrigger",
  "value",
  "validation",
  "validationErrorMessage",
  "metadata",
  "pdfLocation",
];

/**
 * Canonical key order for `metadata.other.checkbox` / `.radio` — the "Other"
 * tick itself. Same path-dependence as `other.input`: republishing from Studio
 * emits the DB's alphabetical copy (`pdfLocation text value`) while applying a
 * local bundle keeps the authored order, so the box moved to the top of the
 * object depending on which script last published.
 *
 * `pdfLocation` goes last for the same reason it does on the field itself: it
 * is the bulkiest key and reads better after the mark's text and value.
 */
const OTHER_MARK_KEY_ORDER = ["text", "value", "pdfLocation"];

/**
 * Canonical key order for a section's `metadata.meviDealRoleRules`.
 *
 * `write` before `read` — analysts read these raw and the writable roles are
 * the ones that matter first. Left alone, the order came from whatever the
 * source carried: republishing from Studio emitted the DB's alphabetical copy
 * (`read write`) while applying a local bundle kept the authored order, so the
 * block flipped depending on which path last published. The authored bundles
 * are not self-consistent either (CO has both orders, plus some with
 * `skipIfCreatorNot` first), so this settles it in one place.
 *
 * `skipIfCreatorNot` leads both: it gates whether the rule applies at all, so it reads before
 * the roles it would grant. All 8 authored occurrences in CO put it first; only the DB's
 * alphabetical copy moves it, and the earlier write/read normalisation pushed it to the end.
 */
const reorderMeviDealRoleRules = (rules) => {
  if (!rules || typeof rules !== "object" || Array.isArray(rules)) return rules;
  const out = {};
  for (const k of ["skipIfCreatorNot", "write", "read"]) {
    if (Object.prototype.hasOwnProperty.call(rules, k)) out[k] = rules[k];
  }
  for (const k of Object.keys(rules)) {
    if (!Object.prototype.hasOwnProperty.call(out, k)) out[k] = rules[k];
  }
  return out;
};

/** Section metadata, with `meviDealRoleRules` normalized. Other keys untouched. */
const reorderSectionMetadata = (meta) => {
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) return meta;
  if (!meta.meviDealRoleRules) return meta;
  return { ...meta, meviDealRoleRules: reorderMeviDealRoleRules(meta.meviDealRoleRules) };
};

const reorderOtherMark = (mark) => {
  if (!mark || typeof mark !== "object" || Array.isArray(mark)) return mark;
  const out = {};
  for (const k of OTHER_MARK_KEY_ORDER) {
    if (Object.prototype.hasOwnProperty.call(mark, k)) out[k] = mark[k];
  }
  for (const k of Object.keys(mark)) {
    if (!Object.prototype.hasOwnProperty.call(out, k)) out[k] = mark[k];
  }
  return out;
};

const reorderOtherInput = (input) => {
  if (!input || typeof input !== "object" || Array.isArray(input)) return input;
  const order =
    "order" in input || "type" in input
      ? OTHER_INPUT_KEY_ORDER_FULL
      : OTHER_INPUT_KEY_ORDER_SLIM;
  const out = {};
  for (const k of order) {
    if (!Object.prototype.hasOwnProperty.call(input, k)) continue;
    out[k] = k === "validation" ? reorderValidation(input[k]) : input[k];
  }
  // Anything unrecognised keeps its relative position at the end rather than
  // being dropped.
  for (const k of Object.keys(input)) {
    if (!Object.prototype.hasOwnProperty.call(out, k)) out[k] = input[k];
  }
  return out;
};

/**
 * Canonical key order for the `metadata.other` container itself.
 *
 * The mark (`checkbox` / `radio` / `decisionValue`) names the option that turns the "Other" row
 * on, so it reads before the input that row reveals. The seed was never self-consistent here —
 * checkbox led its input 626 times but trailed it 3, radio led 53 and trailed 39 — and a
 * republish from the DB alphabetises the lot, which puts `input` ahead of `radio` every time.
 */
const OTHER_KEY_ORDER = ["checkbox", "radio", "decisionValue", "input"];

const reorderOther = (other) => {
  if (!other || typeof other !== "object" || Array.isArray(other)) return other;
  const reorderByKey = (k, v) => {
    if (k === "input") return reorderOtherInput(v);
    if (k === "checkbox" || k === "radio") return reorderOtherMark(v);
    return v;
  };
  const out = {};
  for (const k of OTHER_KEY_ORDER) {
    if (Object.prototype.hasOwnProperty.call(other, k)) out[k] = reorderByKey(k, other[k]);
  }
  for (const k of Object.keys(other)) {
    if (!Object.prototype.hasOwnProperty.call(out, k)) out[k] = other[k];
  }
  return out;
};

/**
 * Canonical key order for `metadata.calculation[]` entries. The authored seed is unanimous
 * (`order, action, sum` / `order, action, field`); only a DB round-trip disturbs it, hoisting
 * `action` alphabetically above `order`.
 */
const CALCULATION_KEY_ORDER = ["order", "action"];

const reorderCalculation = (calc) => {
  if (!Array.isArray(calc)) return calc;
  return calc.map((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return entry;
    const out = {};
    for (const k of CALCULATION_KEY_ORDER) {
      if (Object.prototype.hasOwnProperty.call(entry, k)) out[k] = entry[k];
    }
    for (const k of Object.keys(entry)) {
      if (!Object.prototype.hasOwnProperty.call(out, k)) out[k] = entry[k];
    }
    return out;
  });
};

const reorderFieldMetadata = (meta) => {
  if (!meta || typeof meta !== "object") return meta;
  // pdfLocation is pulled out and re-added last: it is the bulkiest key and reads better after
  // the field's settings. Its position was previously whatever insertion order the object
  // happened to carry — the datetime branch spreads defaults over existing metadata, which is why
  // fieldData could land after it. Nested rects (options[], other.input, overflow[]) are left
  // where they are; only the field's own box moves.
  const { options, other, clearValue, fieldData, content, format, customTemplateDoNotCopy, pdfLocation, ...rest } = meta;
  const result = {};
  if (options !== undefined) result.options = options;
  if (other !== undefined) result.other = reorderOther(other);
  if (clearValue !== undefined) result.clearValue = clearValue;
  if (fieldData !== undefined) result.fieldData = fieldData;
  if (content !== undefined) result.content = reorderContent(content);
  // `format` leads the remaining settings and `customTemplateDoNotCopy` trails them (with
  // pdfLocation still last). Both hold with zero exceptions across the authored CO seed, but a
  // template republished from the DB arrives alphabetised — Go marshals map[string]interface{}
  // in sorted key order — which is what dragged customTemplateDoNotCopy up above `format`.
  if (format !== undefined) result.format = format;
  Object.assign(result, rest);
  if (Array.isArray(result.calculation)) result.calculation = reorderCalculation(result.calculation);
  if (customTemplateDoNotCopy !== undefined) result.customTemplateDoNotCopy = customTemplateDoNotCopy;
  if (pdfLocation !== undefined) result.pdfLocation = pdfLocation;
  return result;
};

/** Guarantee `mecOnly` is emitted immediately after `mec` in field metadata, regardless of insertion order from spreads/round-trips. */
const orderMecOnlyAfterMec = (meta) => {
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) return meta;
  if (!("mec" in meta) || !("mecOnly" in meta)) return meta;
  const out = {};
  for (const k of Object.keys(meta)) {
    if (k === "mecOnly") continue; // re-inserted directly after `mec`
    out[k] = meta[k];
    if (k === "mec") out.mecOnly = meta.mecOnly;
  }
  return out;
};

const generateSummaryJSON = (fields, summaryType) => {
  // Component fields tagged with combinedDate/combinedTime roll up into ONE
  // entry per combined key (the individual components are NOT emitted). The
  // rollup shows in this summary if any component participates; display=true
  // if any component's summary flag is true. Everything else emits as before.
  const rollups = {}; // combinedKey -> { label, type, anyDefined, anyDisplay }
  const singles = [];
  for (const field of fields) {
    const isTime = field.combinedTime != null && field.combinedTime !== "";
    const isDate = field.combinedDate != null && field.combinedDate !== "";
    if (isTime || isDate) {
      const key = isTime ? field.combinedTime : field.combinedDate;
      const label = isTime ? field.combinedTimeLabel : field.combinedDateLabel;
      const r = rollups[key] || (rollups[key] = { label: label ?? null, type: isTime ? "time" : "date", anyDefined: false, anyDisplay: false });
      if (label != null) r.label = label;
      if (field[summaryType] !== undefined) r.anyDefined = true;
      if (field[summaryType] === true) r.anyDisplay = true;
      continue; // component fields never emit individually
    }
    if (field[summaryType] !== undefined) singles.push(field);
  }

  const entries = [];
  for (const [key, r] of Object.entries(rollups)) {
    if (!r.anyDefined) continue; // no component participates in this summary
    entries.push([key, { label: r.label, type: r.type, display: r.anyDisplay }]);
  }
  for (const field of singles) {
    entries.push([field.attributeName, {
      label: field[summaryType + "Label"] !== undefined ? field[summaryType + "Label"] : (field.label ?? null),
      type: field[summaryType + "Type"] !== undefined ? field[summaryType + "Type"] : field.type,
      display: field[summaryType],
    }]);
  }
  entries.sort((a, b) => {
    const ad = a[1].display === true ? 0 : 1, bd = b[1].display === true ? 0 : 1;
    if (ad !== bd) return ad - bd;
    return a[0].localeCompare(b[0]);
  });
  return Object.fromEntries(entries);
};

/** Merge `lines` from editor state into pdfLocation for export; canonical rect key order. */
const packAdditionalPdfLocation = (item) => {
  const pl = item?.pdfLocation;
  if (!pl || typeof pl !== "object") return pl;
  if (Array.isArray(pl)) {
    return pl.map((rect) =>
      rect && typeof rect === "object" ? normalizePdfLocationRectForPublish({ ...rect }) : rect,
    );
  }
  const packed = {
    ...pl,
    ...(item.lines != null && item.lines > 0 && { lines: item.lines }),
  };
  return normalizePdfLocationRectForPublish(packed);
};

const buildAdditionalFieldsJSON = (additionalFields) =>
  buildAdditionalFieldsJSONPublished(additionalFields, packAdditionalPdfLocation);

/**
 * Single form field → published JSON field object (same shape as formData[].sections[].fields[]).
 * @param {Object} field - Client field
 * @param {Array} allFields - Full field list (for metadata.overflow resolution)
 * @param {number} [fallbackOrder] - 1-based position in the section, used when
 *   `field.order` is missing or unusable (see toFieldOrder)
 * @returns {Object} Published field
 */
export const buildPublishedFieldOutput = (field, allFields = [], fallbackOrder = undefined) => {
  const overflowN = (attrName) => {
    const m = (attrName || "").match(/_overflow(\d+)$/);
    return m ? parseInt(m[1], 10) : 0;
  };

  let cleanMetadata = null;
  if (field.metadata) {
    cleanMetadata = { ...field.metadata };
    delete cleanMetadata.formUploadData;
    delete cleanMetadata.offerCompareData;
    delete cleanMetadata.pdfLocationContinuations;
    if (
      !["radio", "checkbox", "radioWithOther", "checkboxWithOther", "decision", "decisionWithOther"].includes(
        field.type,
      )
    ) {
      delete cleanMetadata.options;
    }
    if (cleanMetadata.options) {
      cleanMetadata.options = cleanMetadata.options.map(({ value, text, ...optRest }) => ({
        ...(value !== undefined && { value }),
        ...(text !== undefined && { text }),
        ...optRest,
      }));
    }
    cleanMetadata = reorderFieldMetadata(cleanMetadata);
    if (field.annotationMultiline > 0 && cleanMetadata?.pdfLocation) {
      cleanMetadata = {
        ...cleanMetadata,
        pdfLocation: { ...cleanMetadata.pdfLocation, lines: field.annotationMultiline },
      };
    }
    const overflowFields = (allFields || []).filter((f) => f.isOverflow === field.attributeName);
    overflowFields.sort((a, b) => overflowN(a.attributeName) - overflowN(b.attributeName));
    if (overflowFields.length > 0) {
      const overflow = overflowFields.map((f) => ({
        attributeName: f.attributeName,
        pdfLocation: {
          ...f.metadata?.pdfLocation,
          lines: f.annotationMultiline ?? f.metadata?.pdfLocation?.lines ?? 0,
        },
      }));
      cleanMetadata = { ...cleanMetadata, overflow };
    }
    if (Object.keys(cleanMetadata).length === 0) {
      cleanMetadata = null;
    }
  }
  if (!cleanMetadata) {
    const overflowFieldsForTarget = (allFields || []).filter((f) => f.isOverflow === field.attributeName);
    overflowFieldsForTarget.sort((a, b) => overflowN(a.attributeName) - overflowN(b.attributeName));
    if (overflowFieldsForTarget.length > 0) {
      const overflow = overflowFieldsForTarget.map((f) => ({
        attributeName: f.attributeName,
        pdfLocation: {
          ...f.metadata?.pdfLocation,
          lines: f.annotationMultiline ?? f.metadata?.pdfLocation?.lines ?? 0,
        },
      }));
      cleanMetadata = { overflow };
    }
  }

  const validation = {
    min: field.validation?.min || null,
    max: field.validation?.max || null,
    minLength: field.validation?.minLength || null,
    maxLength: field.validation?.maxLength || null,
    patterns: field.validation?.patterns || null,
    // `??` not `||`: an explicit `required: false` is a real authored value
    // and must not collapse to null on a round-trip.
    required: field.validation?.required ?? null,
  };

  const fieldOutput = {
    order: toFieldOrder(field.order, fallbackOrder),
    type: field.type,
    constraint: field.constraint || null,
    constraintId: field.constraintId || null,
    placeholder: field.placeholder || null,
    info: field.info || null,
    hint: field.hint || null,
    ...(field.moreDetails && { moreDetails: field.moreDetails }),
    label: field.label,
    ...(field.labelFormUpload && { labelFormUpload: field.labelFormUpload }),
    attributeName: field.attributeName,
    ...(field.id != null && field.id !== "" && { id: field.id }),
    ...(field.linkedFieldId != null &&
      field.linkedFieldId !== "" && { linkedFieldId: field.linkedFieldId }),
    eventTrigger: field.eventTrigger || null,
    value: field.value || null,
    // Combined-deadline rollup keys (see generateSummaryJSON): component
    // fields keep their own input but roll up into one summary entry.
    ...(field.combinedTime != null && field.combinedTime !== "" && { combinedTime: field.combinedTime }),
    ...(field.combinedTimeLabel != null && field.combinedTimeLabel !== "" && { combinedTimeLabel: field.combinedTimeLabel }),
    ...(field.combinedDate != null && field.combinedDate !== "" && { combinedDate: field.combinedDate }),
    ...(field.combinedDateLabel != null && field.combinedDateLabel !== "" && { combinedDateLabel: field.combinedDateLabel }),
    validation,
    validationErrorMessage: field.validationErrorMessage || null,
  };

  // Emit ONLY the display booleans on the field. The label / type
  // values still travel into `formSummaryJSON` / `dealSummaryJSON`
  // via `generateSummaryJSON` (which reads `field[summaryType+"Label"]`
  // with a fallback to `field.label`) — emitting them here too would
  // duplicate the data on every round-trip and bloat the output.
  if (field.dealSummary !== undefined) fieldOutput.dealSummary = field.dealSummary;
  if (field.formSummary !== undefined) fieldOutput.formSummary = field.formSummary;

  if (field.fieldNotes && field.fieldNotes.trim()) {
    fieldOutput.fieldNotes = field.fieldNotes.trim();
  }

  if (field.readOnly) {
    fieldOutput.readOnly = true;
  }

  // Preserve-only: no studio UI edits `disabled`, but it must survive the
  // round-trip. Emitted only when truthy, matching the published shape.
  if (field.disabled) {
    fieldOutput.disabled = true;
  }

  fieldOutput.metadata = cleanMetadata;

  if (field.customTemplateDoNotCopy) {
    fieldOutput.customTemplateDoNotCopy = true;
  }

  if (field.editableAnnotation != null && typeof field.editableAnnotation === "object" && !Array.isArray(field.editableAnnotation)) {
    const { meviDealRoles: rolesIn, ...eaRest } = field.editableAnnotation;
    const meviDealRoles = Array.isArray(rolesIn)
      ? rolesIn.map((r) => String(r || "").trim()).filter(Boolean)
      : [];
    fieldOutput.editableAnnotation = { ...eaRest, meviDealRoles };
  }

  if (field.fieldData) {
    fieldOutput.fieldData = field.fieldData;
  }

  if (field.type === "datetime") {
    if (!fieldOutput.metadata) {
      fieldOutput.metadata = {};
    }

    const datetimeDefaults = {
      displayFormat: null,
      pickerFormat: null,
      minDateAttributeName: null,
      maxDateAttributeName: null,
    };

    fieldOutput.metadata = {
      ...datetimeDefaults,
      ...fieldOutput.metadata,
      fieldData: {
        format: fieldOutput.metadata.fieldData?.format || field.metadata?.fieldData?.format || "01/02/2006",
        ...fieldOutput.metadata.fieldData,
      },
    };

    if (field.metadata?.mec !== undefined && field.metadata?.mec !== null) {
      fieldOutput.metadata.mec = field.metadata.mec;
    }

    if (field.metadata?.mecOnly === true) {
      fieldOutput.metadata.mecOnly = true;
    }
  }

  if (field.type === "checkboxWithOther" || field.type === "radioWithOther") {
    if (fieldOutput.metadata?.other) {
      const checkboxOrRadioValue =
        fieldOutput.metadata.other.checkbox?.value || fieldOutput.metadata.other.radio?.value || "";
      if (checkboxOrRadioValue && fieldOutput.metadata.other.input) {
        fieldOutput.metadata.other.input.attributeName = `${checkboxOrRadioValue}Input`;
      }
    }
  }

  if (fieldOutput.metadata) {
    fieldOutput.metadata = normalizeMetadataPdfLocationsForPublish(fieldOutput.metadata);
    fieldOutput.metadata = orderMecOnlyAfterMec(fieldOutput.metadata);
  }

  return fieldOutput;
};

/**
 * Published form field objects for one signer row from persona bindings (Extra PDF → signaturesJSON).
 * Groups placements by linkedFieldId or attributeName; merges rects (array when multiple).
 */
const buildSignerRowFieldsFromPlacements = (placements, allFields = []) => {
  if (!placements || placements.length === 0) return [];
  const order = [];
  const groups = new Map();
  for (const row of placements) {
    const gid =
      row.linkedFieldId != null && row.linkedFieldId !== ""
        ? `id:${row.linkedFieldId}`
        : `attr:${row.attributeName ?? ""}`;
    if (!groups.has(gid)) {
      groups.set(gid, []);
      order.push(gid);
    }
    groups.get(gid).push(row);
  }
  const out = [];
  for (const gid of order) {
    const group = groups.get(gid);
    const first = group[0];
    const packedLocs = group.map((g) => packAdditionalPdfLocation(g));
    const pdfMerged = packedLocs.length === 1 ? packedLocs[0] : packedLocs;
    // Prefer the signer field's own definition snapshot (what the signer field editor edits) so
    // validation/hint/type/etc. round-trip exactly like a regular field. Fall back to a live linked
    // form field, then to a minimal input stub.
    let field = first.def
      ? {
          ...first.def,
          attributeName: first.attributeName || first.def.attributeName || "",
          ...(first.label != null && first.label !== "" ? { label: first.label } : {}),
        }
      : null;
    if (!field) {
      field =
        first.linkedFieldId != null && first.linkedFieldId !== ""
          ? allFields.find((f) => f.id === first.linkedFieldId)
          : null;
    }
    if (!field && first.attributeName) {
      field = allFields.find((f) => f.attributeName === first.attributeName);
    }
    if (field) {
      const published = buildPublishedFieldOutput(field, allFields);
      if (published.order == null) published.order = 1;
      // "text" is the placement-editor placeholder, not a real Mevi field type — coerce it
      // (and any missing type) so it can never leak into a signer field's published output.
      if (!published.type || published.type === "text") published.type = "input";
      const hasOptionLevelPdfLocations = Array.isArray(published.metadata?.options)
        && published.metadata.options.some((opt) => !!opt?.pdfLocation);
      published.metadata = normalizeMetadataPdfLocationsForPublish(
        hasOptionLevelPdfLocations
          ? { ...(published.metadata || {}) }
          : {
              ...(published.metadata || {}),
              pdfLocation: pdfMerged,
            },
      );
      out.push(published);
    } else if (first.attributeName) {
      // Key order MUST match buildPublishedFieldOutput (Path A) so signer
      // fields built from this stub render identically to regular fields and
      // to def-backed signer fields — no per-field key-order drift in the JSON.
      out.push({
        order: 1,
        type: "input",
        constraint: null,
        constraintId: null,
        placeholder: null,
        info: null,
        hint: null,
        // Don't fall back to attributeName — only emit a real, author-typed label.
        label: first.label != null && first.label !== "" ? first.label : "",
        attributeName: first.attributeName,
        eventTrigger: null,
        value: null,
        validation: {
          min: null,
          max: null,
          minLength: null,
          maxLength: null,
          patterns: null,
          required: null,
        },
        validationErrorMessage: null,
        metadata: normalizeMetadataPdfLocationsForPublish({
          pdfLocation: pdfMerged,
        }),
      });
    }
  }
  // Sequence by the author's `order`. Array.prototype.sort is stable, so rows sharing an order
  // keep the sequence they were built in — which is every field today, since the whole corpus is
  // `order: 1`. That makes this inert until an author sets real orders, and it is what lets the
  // published fields[] follow the author rather than binding insertion order.
  const orderOf = (f) => (Number.isFinite(Number(f?.order)) ? Number(f.order) : Number.MAX_SAFE_INTEGER);
  out.sort((a, b) => orderOf(a) - orderOf(b));
  return out;
};

const generateSignaturesJSON = (
  signatures,
  signatureAnnotations,
  signerAttributeBindings = [],
  allFormFields = [],
) => {
  if (!signatures || Object.keys(signatures).length === 0) return {};

  // Grouped, not overwritten: a signer can have several tabs and the last one used to win,
  // silently discarding the rest.
  const annotationsByRoleSigner = (signatureAnnotations || []).reduce((acc, entry) => {
    if (entry?.roleKey && entry?.signerKey && entry?.pdfLocation) {
      const key = `${entry.roleKey}\t${entry.signerKey}`;
      (acc[key] = acc[key] || []).push(entry.pdfLocation);
    }
    return acc;
  }, {});

  const placementsByRoleSigner = (signerAttributeBindings || []).reduce((acc, b) => {
    if (!b?.roleKey || !b?.signerKey) return acc;
    const key = `${b.roleKey}\t${b.signerKey}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push({
      attributeName: b.attributeName ?? "",
      pdfLocation: b.pdfLocation && typeof b.pdfLocation === "object" ? { ...b.pdfLocation } : {},
      lines: b.lines,
      linkedFieldId: b.linkedFieldId,
      label: b.label,
      def: b.def,
    });
    return acc;
  }, {});

  const attachSignerFields = (row, roleKey, signerKey) => {
    const placements = placementsByRoleSigner[`${roleKey}\t${signerKey}`];
    if (!placements || placements.length === 0) return row;
    const signerFields = buildSignerRowFieldsFromPlacements(placements, allFormFields);
    if (signerFields.length === 0) return row;
    return { ...row, fields: signerFields };
  };

  return Object.fromEntries(
    Object.entries(signatures).map(([roleKey, roleValue]) => {
      const roleSigs = roleValue.signatures || [];
      const mappedSignatures = roleSigs.map((signature) => {
        const tabs = annotationsByRoleSigner[`${roleKey}\t${signature.signerKey}`];
        let row = {
          signerKey: signature.signerKey,
          email: signature.email,
          ...publishTabLocations(tabs),
        };
        row = attachSignerFields(row, roleKey, signature.signerKey);
        if (
          (!row.fields || row.fields.length === 0) &&
          Array.isArray(signature.fields) &&
          signature.fields.length > 0
        ) {
          row = { ...row, fields: JSON.parse(JSON.stringify(signature.fields)) };
        }
        return row;
      });
      const mappedSignerKeys = new Set(mappedSignatures.map((s) => s.signerKey));
      (signatureAnnotations || [])
        .filter(
          (entry) =>
            entry.roleKey === roleKey &&
            entry.signerKey &&
            entry.pdfLocation &&
            !mappedSignerKeys.has(entry.signerKey),
        )
        .forEach((entry) => {
          let row = {
            signerKey: entry.signerKey,
            email: "",
            ...publishTabLocations([entry.pdfLocation]),
          };
          row = attachSignerFields(row, roleKey, entry.signerKey);
          mappedSignatures.push(row);
          mappedSignerKeys.add(entry.signerKey);
        });

      Object.keys(placementsByRoleSigner)
        .filter((k) => k.startsWith(`${roleKey}\t`))
        .forEach((k) => {
          const signerKey = k.split("\t")[1];
          if (!mappedSignerKeys.has(signerKey)) {
            const placements = placementsByRoleSigner[k];
            if (placements && placements.length > 0) {
              let row = { signerKey, email: "" };
              row = attachSignerFields(row, roleKey, signerKey);
              mappedSignatures.push(row);
              mappedSignerKeys.add(signerKey);
            }
          }
        });

      const { signatures: _, ...roleRest } = roleValue;
      return [roleKey, { ...roleRest, signatures: mappedSignatures }];
    }),
  );
};

const generateInitialsJSON = (
  signatures,
  initialsAnnotations = [],
  initialerAttributeBindings = [],
  allFormFields = [],
  storedInitials = null,
) => {
  // Roles come from the UNION of signaturesJSON and whatever is initialled. A role can be
  // initialled without being signed — and prune removes signature slots that have no tab, which
  // can take the whole role out of `signatures`. Iterating `signatures` alone silently dropped
  // those initials from the published form.
  const initialsRoleKeys = new Set(
    [
      ...(initialsAnnotations || []).map((a) => a?.roleKey),
      ...(initialerAttributeBindings || []).map((b) => b?.roleKey),
    ].filter(Boolean),
  );
  if (
    (!signatures || Object.keys(signatures).length === 0) &&
    initialsRoleKeys.size === 0
  ) {
    return {};
  }

  // Grouped, not overwritten — see generateSignaturesJSON.
  const annotationsByRoleSigner = (initialsAnnotations || []).reduce((acc, entry) => {
    if (entry?.roleKey && entry?.signerKey && entry?.pdfLocation) {
      const key = `${entry.roleKey}\t${entry.signerKey}`;
      (acc[key] = acc[key] || []).push(entry.pdfLocation);
    }
    return acc;
  }, {});

  const placementsByRoleSigner = (initialerAttributeBindings || []).reduce((acc, b) => {
    if (!b?.roleKey || !b?.signerKey) return acc;
    const key = `${b.roleKey}\t${b.signerKey}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push({
      attributeName: b.attributeName ?? "",
      pdfLocation: b.pdfLocation && typeof b.pdfLocation === "object" ? { ...b.pdfLocation } : {},
      lines: b.lines,
      linkedFieldId: b.linkedFieldId,
      label: b.label,
      def: b.def,
    });
    return acc;
  }, {});

  const attachSignerFields = (row, roleKey, signerKey) => {
    const placements = placementsByRoleSigner[`${roleKey}\t${signerKey}`];
    if (!placements || placements.length === 0) return row;
    const signerFields = buildSignerRowFieldsFromPlacements(placements, allFormFields);
    if (signerFields.length === 0) return row;
    return { ...row, fields: signerFields };
  };
  const rowHasInitialsPayload = (row) =>
    !!(
      row?.pdfLocation ||
      // A multi-tab signer carries `pdfLocations[]` instead — without this the row reads as empty
      // and is dropped, losing every tab.
      (Array.isArray(row?.pdfLocations) && row.pdfLocations.length > 0) ||
      (Array.isArray(row?.fields) && row.fields.length > 0)
    );

  // Role metadata precedence: the form's own stored initialsJSON first, then the matching
  // signatures role, then the standard role definition.
  //
  // Stored initials come FIRST because they are the only lossless source. `altSigners` is
  // author-set per role and appears nowhere else — synthesising a role from the template
  // silently replaced ["client.sell"] with [] — and a seed `default` may legitimately differ
  // from the template's. Signatures remain the fallback for a role initialled for the first
  // time in this edit, which has no stored entry yet.
  const roleTemplate = buildSignaturesTemplate();
  const stored = storedInitials && typeof storedInitials === "object" ? storedInitials : {};
  const roleMetaOf = (roleKey) => {
    const src = stored[roleKey] || (signatures || {})[roleKey] || roleTemplate[roleKey] || {};
    // Rows are rebuilt from annotations/bindings below; max and additionalSignaturePage are
    // recomputed from them. Only the author-owned properties are inherited.
    const { signatures: _s, initials: _i, max: _m, additionalSignaturePage: _a, ...meta } = src;
    return meta;
  };
  // Emails live on the row, and a row rebuilt from an annotation has none. Recover it from the
  // stored initials row, then the signature row, before falling back to the studio convention.
  const storedEmail = (roleKey, signerKey) => {
    const rows = stored[roleKey]?.initials ?? stored[roleKey]?.signatures ?? [];
    const hit = rows.find((r) => r?.signerKey === signerKey);
    if (hit?.email) return hit.email;
    const sig = ((signatures || {})[roleKey]?.signatures || []).find((r) => r?.signerKey === signerKey);
    if (sig?.email) return sig.email;
    return signerKey ? `${signerKey}@mevi.io` : "";
  };
  const orderedRoleKeys = [
    ...Object.keys(signatures || {}),
    ...Object.keys(stored).filter((k) => !(signatures || {})[k]),
    ...[...initialsRoleKeys].filter((k) => !(signatures || {})[k] && !stored[k]),
  ];

  return Object.fromEntries(
    orderedRoleKeys
      .map((roleKey) => {
      const roleSigs = (signatures || {})[roleKey]?.signatures || [];
      const mappedInitials = roleSigs.map((signature) => {
        const tabs = annotationsByRoleSigner[`${roleKey}\t${signature.signerKey}`];
        let row = {
          signerKey: signature.signerKey,
          email: signature.email || storedEmail(roleKey, signature.signerKey),
          ...publishTabLocations(tabs),
        };
        row = attachSignerFields(row, roleKey, signature.signerKey);
        // NB: NO fallback to `signature.fields` here. Those fields
        // belong to `signaturesJSON`, not initials. Initials data
        // comes ONLY from `initialerAttributeBindings` /
        // `initialsAnnotations`. If a signer has neither, this row
        // gets filtered out below by `rowHasInitialsPayload`.
        return row;
      });
      const mappedSignerKeys = new Set(mappedInitials.map((s) => s.signerKey));
      (initialsAnnotations || [])
        .filter(
          (entry) =>
            entry.roleKey === roleKey &&
            entry.signerKey &&
            entry.pdfLocation &&
            !mappedSignerKeys.has(entry.signerKey),
        )
        .forEach((entry) => {
          let row = {
            signerKey: entry.signerKey,
            email: storedEmail(roleKey, entry.signerKey),
            ...publishTabLocations([entry.pdfLocation]),
          };
          row = attachSignerFields(row, roleKey, entry.signerKey);
          mappedInitials.push(row);
          mappedSignerKeys.add(entry.signerKey);
        });

      Object.keys(placementsByRoleSigner)
        .filter((k) => k.startsWith(`${roleKey}\t`))
        .forEach((k) => {
          const signerKey = k.split("\t")[1];
          if (!mappedSignerKeys.has(signerKey)) {
            const placements = placementsByRoleSigner[k];
            if (placements && placements.length > 0) {
              let row = { signerKey, email: storedEmail(roleKey, signerKey) };
              row = attachSignerFields(row, roleKey, signerKey);
              mappedInitials.push(row);
              mappedSignerKeys.add(signerKey);
            }
          }
        });

      const roleRest = roleMetaOf(roleKey);
      const prunedInitials = mappedInitials.filter(rowHasInitialsPayload);
      if (prunedInitials.length === 0) return null;
      // Counts come from the initials rows, not the signature ones. roleRest is inherited from
      // `signatures`, so without this an initials role would advertise the signature role's max —
      // and there are only as many places to initial as there are rows left after the filter.
      // `initials`, not `signatures`: an initials role's rows are initials. Older bundles used
      // `signatures` here and the reader still accepts it, so an old form republishes into the
      // new shape (utils/signatureUtils.js signerRowsOf).
      // Fixed key order, matching what signaturesJSON roles publish: a diff of two bundles
      // should show real changes, not a reshuffle. max/additionalSignaturePage are recomputed
      // from the rows, so they cannot simply ride along in roleRest's original position.
      return [
        roleKey,
        {
          integrationRole: roleRest.integrationRole,
          meviDealRole: roleRest.meviDealRole,
          min: roleRest.min,
          max: prunedInitials.length,
          // Same rule as signature roles: max is however many slots exist, and a deal starts
          // with two initialers unless there is only one slot to initial.
          default: prunedInitials.length >= 2 ? 2 : 1,
          additionalSignaturePage: prunedInitials.length + 1,
          altSigners: roleRest.altSigners ?? [],
          initials: prunedInitials,
        },
      ];
    })
      .filter(Boolean),
  );
};

/**
 * subsequentSigningJSON: an object keyed by meviDealRole, shaped exactly like signaturesJSON.
 *
 * A block is `{integrationRole, meviDealRole, min, max, default, altSigners, label, actionLabel,
 * key, signatures: [{signerKey, email, fields}]}`. Studio carries the whole block verbatim, so the
 * three keys signaturesJSON does not have — `label`, `actionLabel`, `key` — ride through on the
 * role rest the same way `altSigners` and `integrationRole` do, with no settings-panel UI of
 * their own. They are edited in JSON Preview.
 *
 * Fields are attached exactly as generateSignaturesJSON attaches them: bindings win, and a row with
 * no bindings keeps its stored fields. There is no signature-annotation pass — a subsequent block
 * has no sign tab of its own.
 */
const generateSubsequentSigningJSON = (
  subsequentSigning,
  subsequentSigningAttributeBindings = [],
  allFormFields = [],
) => {
  if (!subsequentSigning || typeof subsequentSigning !== "object" || Array.isArray(subsequentSigning)) return {};
  if (Object.keys(subsequentSigning).length === 0) return {};

  const placementsByRoleSigner = (subsequentSigningAttributeBindings || []).reduce((acc, b) => {
    if (!b?.roleKey || !b?.signerKey) return acc;
    const key = `${b.roleKey}\t${b.signerKey}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push({
      attributeName: b.attributeName ?? "",
      pdfLocation: b.pdfLocation && typeof b.pdfLocation === "object" ? { ...b.pdfLocation } : {},
      lines: b.lines,
      linkedFieldId: b.linkedFieldId,
      label: b.label,
      def: b.def,
    });
    return acc;
  }, {});

  const attachSignerFields = (row, roleKey, signerKey) => {
    const placements = placementsByRoleSigner[`${roleKey}\t${signerKey}`];
    if (!placements || placements.length === 0) return row;
    const signerFields = buildSignerRowFieldsFromPlacements(placements, allFormFields);
    if (signerFields.length === 0) return row;
    return { ...row, fields: signerFields };
  };

  return Object.fromEntries(
    Object.entries(subsequentSigning).map(([roleKey, roleValue]) => {
      const { signatures: roleSigs, ...roleRest } = roleValue || {};
      const mappedSignatures = (roleSigs || []).map((signature) => {
        let row = { signerKey: signature.signerKey, email: signature.email };
        row = attachSignerFields(row, roleKey, signature.signerKey);
        if (
          (!row.fields || row.fields.length === 0) &&
          Array.isArray(signature.fields) &&
          signature.fields.length > 0
        ) {
          row = { ...row, fields: JSON.parse(JSON.stringify(signature.fields)) };
        }
        return row;
      });
      return [roleKey, { ...roleRest, signatures: mappedSignatures }];
    }),
  );
};

/**
 * Generates the complete Form JSON (same output as Preview → Download Form JSON).
 *
 * @param {Object} opts
 * @param {Array} opts.fields
 * @param {Array} opts.sections
 * @param {Object} opts.signatures
 * @param {Array} opts.selectedCreators
 * @param {Object} opts.formDetails
 * @param {boolean} opts.lineNumbers
 * @param {Array} opts.formUploadSections
 * @param {Array} opts.signatureAnnotations
 * @param {Array} opts.signerAttributeBindings persona PDF text bindings → `fields` on each signaturesJSON signer row
 * @param {Object} opts.subsequentSigning → formJSON.subsequentSigningJSON (role-keyed, signaturesJSON-shaped)
 * @param {Array} opts.subsequentSigningAttributeBindings subsequent PDF text bindings → `fields` on each subsequentSigningJSON signer row
 * @param {boolean} opts.isPurchaseContract
 * @returns {string} JSON string
 */
export const generateFormJson = ({
  fields,
  sections,
  signatures = {},
  selectedCreators = [],
  formDetails = {},
  lineNumbers = false,
  formUploadSections = [],
  signatureAnnotations = [],
  initialsAnnotations = [],
  additionalFields = [],
  signerAttributeBindings = [],
  initialerAttributeBindings = [],
  subsequentSigning = {},
  subsequentSigningAttributeBindings = [],
  isPurchaseContract = false,
  extraFormSummary = {},
  extraDealSummary = {},
  rulesJSON = null,
  // The form's stored initialsJSON — role metadata + row emails. See generateInitialsJSON.
  initials = null,
}) => {
  const orderedSections = [...sections].map((section, index) => ({
    ...section,
    originalIndex: index,
  }));

  // Generate form upload data arrays and settings based on sections
  const formUploadDataArrays = {};
  const formUploadSettings = {};

  // One loop for any number of upload sections. This was previously three
  // near-identical branches for lengths 1, 2 and 3 with no `else`, so a form
  // with FOUR sections emitted nothing at all and its `formUploadData*` arrays
  // were silently lost on every round-trip (CO 0002, 0003, 0055, 0056).
  //
  // Key shape is unchanged: the first array is `formUploadData` (no suffix)
  // while its settings key is `formUploadData1`; later ones are suffixed by
  // position. A single section emits no settings block, matching the old
  // behaviour and the published shape.
  formUploadSections.forEach((section, index) => {
    const dataKey = index === 0 ? "formUploadData" : `formUploadData${index + 1}`;

    const resolved = (section.fieldIds || [])
      .map((fid) => fields.find((f) => f.id === fid)?.attributeName)
      .filter(Boolean);

    // `section.attrNames` is what the template published. Entries naming data
    // gathered elsewhere (e.g. `earnestMoneyHolder` on a Counter Proposal)
    // have no field here, so they never survive the id round-trip — re-emit
    // them in their original position and append anything newly added.
    const published = section.attrNames || [];
    const resolvedSet = new Set(resolved);
    formUploadDataArrays[dataKey] = published.length
      ? [
          ...published.filter((name) => resolvedSet.has(name) || !fields.some((f) => f.attributeName === name)),
          ...resolved.filter((name) => !published.includes(name)),
        ]
      : resolved;

    if (formUploadSections.length > 1) {
      formUploadSettings[`formUploadData${index + 1}`] = {
        title: section.title,
        description: section.description,
      };
    }
  });

  // `sigOnly` (view/share/sign) and `shareOnly` (view/share) forms are the PDF
  // itself — there is no form to fill in, so `formData[0].sections` publishes
  // empty and there is no section to start at. Editor state is left alone: the
  // flag can be turned back off and any authored sections publish again.
  const sectionsSuppressed =
    formDetails.settings?.sigOnly === true || formDetails.settings?.shareOnly === true;

  // Honour an authored start section when it still exists — a form may begin
  // somewhere other than its first section (CO 0021 starts at its third).
  // Falls back to the first section when unset or when the named section has
  // since been removed, which is the long-standing behaviour.
  const authoredStart = formDetails.startSectionAttributeName;
  const authoredStartExists =
    !!authoredStart && sections.some((s) => s.attributeName === authoredStart);
  const startSectionAttributeName = sectionsSuppressed
    ? null
    : (authoredStartExists ? authoredStart : sections[0]?.attributeName) || null;

  const subsequentSigningJSON = generateSubsequentSigningJSON(
    subsequentSigning,
    subsequentSigningAttributeBindings,
    fields,
  );
  const signaturesJSON = generateSignaturesJSON(
    signatures,
    signatureAnnotations,
    signerAttributeBindings,
    fields,
  );
  const initialsJSON = generateInitialsJSON(
    signatures,
    initialsAnnotations,
    initialerAttributeBindings,
    fields,
    initials,
  );
  const additionalFieldsJSON = buildAdditionalFieldsJSON(additionalFields);

  const form = {
    id: formDetails.formId || "00000000-0000-0000-0000-000000000000",
    version: formDetails.version || `${new Date().getFullYear().toString().slice(-2)}.0.0`,
    metadata: {
      category: formDetails.category || null,
      order: toCategoryOrder(formDetails.metadata?.order),
      meviDealRoleCreator: selectedCreators,
      otherSideCanUpload: formDetails.otherSideCanUpload ?? true,
      // Preserve-only — see round-trip-preservation.md.
      ...(formDetails.transactionBroker != null && {
        transactionBroker: formDetails.transactionBroker,
      }),
      // Cascade defaults shared by every annotation in this template
      // (font, placeholder, checkbox/radio glyph + scale, focused-edit
      // background + opacity, R/O background, locked background). The
      // v2 viewer module reads this off `metadata.annotationStyles` —
      // it MUST round-trip cleanly even when the studio doesn't expose
      // a UI for editing it yet.
      ...(formDetails.annotationStyles && { annotationStyles: formDetails.annotationStyles }),
      ...formUploadDataArrays,
      ...(Object.keys(formUploadSettings).length > 0 && {
        formUploadSettings: formUploadSettings,
      }),
      ...(formDetails.dataExtraction && {
        dataExtraction: reorderDataExtraction(formDetails.dataExtraction),
      }),
      // Key order is fixed at pdfV2 → shareOnly → sigOnly → type → reqs so a diff of two
      // published bundles reads the same way every time; the remaining keys follow in their
      // historical order.
      settings: {
        ...(formDetails.settings?.pdfV2 !== false && {
          pdfV2: true,
        }),
        ...(formDetails.settings?.shareOnly === true && {
          shareOnly: true,
        }),
        ...(formDetails.settings?.sigOnly === true && {
          sigOnly: true,
        }),
        // Rule flags (actionExecutedTerminatesDeal, allowsMultipleSignedInstances) are additive
        // and publish into this same dictionary alongside type/propertyType — see RULE_KEYS in
        // utils/formParser.js. They come last so the key order stays stable across publishes.
        ...((formDetails.settings?.type?.type ||
          formDetails.settings?.type?.propertyType ||
          Object.values(formDetails.settings?.type?.rules || {}).some(Boolean)) && {
          type: {
            ...(formDetails.settings?.type?.type && {
              [formDetails.settings.type.type]: true,
            }),
            ...(formDetails.settings?.type?.propertyType && {
              [formDetails.settings.type.propertyType]: true,
            }),
            ...Object.fromEntries(
              Object.entries(formDetails.settings?.type?.rules || {})
                .filter(([, on]) => !!on)
                .map(([key]) => [key, true]),
            ),
          },
        }),
        ...(formDetails.settings?.reqs &&
          Object.keys(formDetails.settings.reqs).length > 0 && {
          reqs: formDetails.settings.reqs,
        }),
        ...(formDetails.dataExtraction && {
          dataExtractionEnabled: true,
        }),
        ...((formDetails.settings?.daysAfter?.mec ||
          formDetails.settings?.daysAfter?.bs ||
          formDetails.settings?.daysAfter?.bad ||
          formDetails.settings?.daysAfter?.coe ||
          formDetails.settings?.daysAfter?.dbc ||
          // The afterFormExecuted anchor can stand alone — a form may use it with no MEC/BAD/…
          // checkbox set, and the block must still be emitted.
          formDetails.settings?.daysAfter?.afterFormExecutedInfo != null ||
          formDetails.settings?.daysAfter?.afterFormExecutedPreposition != null ||
          formDetails.settings?.daysAfter?.mecAnchorTarget) && {
          daysAfter: {
            notSetValue: 0,
            ...(formDetails.settings?.daysAfter?.mec && {
              mecLabel: "MEC",
              mecInfo:
                "<div class='color-primary fw-600'>MEC</div><div>Number of days from Mutually Executed Contract</div>",
              mecPreposition: formDetails.settings?.daysAfter?.mecPreposition || "after",
            }),
            ...(formDetails.settings?.daysAfter?.bs && {
              absLabel: "ABS",
              absInfo:
                "<div class='color-primary fw-600'>ABS</div><div>Number of days after all Buyer Signatures</div>",
              absPreposition: formDetails.settings?.daysAfter?.bsPreposition || "after",
              absFormUpload: true,
            }),
            ...(formDetails.settings?.daysAfter?.bad && {
              mecLabel: "BAD",
              mecInfo:
                "<div class='color-primary fw-600'>BAD</div><div>Number of days after Binding Agreement Date</div>",
              mecPreposition: formDetails.settings?.daysAfter?.badPreposition || "after",
            }),
            ...(formDetails.settings?.daysAfter?.coe && {
              coeLabel: "COE",
              coeInfo:
                "<div class='color-primary fw-600'>COE</div><div>Number of days after Close of Escrow</div>",
              coePreposition: formDetails.settings?.daysAfter?.coePreposition || "after",
            }),
            ...(formDetails.settings?.daysAfter?.dbc && {
              dbcLabel: "DBC",
              dbcInfo:
                "<div class='color-primary fw-600'>DBC</div><div>Number of days before Closing</div>",
              dbcPreposition: formDetails.settings?.daysAfter?.dbcPreposition || "before",
            }),
            // Authored in JSON, no UI. Emitted last and verbatim so the round-trip is lossless.
            ...(formDetails.settings?.daysAfter?.afterFormExecutedInfo != null && {
              afterFormExecutedInfo: formDetails.settings.daysAfter.afterFormExecutedInfo,
            }),
            ...(formDetails.settings?.daysAfter?.afterFormExecutedPreposition != null && {
              afterFormExecutedPreposition: formDetails.settings.daysAfter.afterFormExecutedPreposition,
            }),
            ...(formDetails.settings?.daysAfter?.mecAnchorTarget && {
              mecAnchorTarget: formDetails.settings.daysAfter.mecAnchorTarget,
            }),
          },
        }),
        // brandedForms (e.g. { maxHeight, maxWidth }) — no UI; round-trip passthrough.
        ...(formDetails.settings?.brandedForms &&
          typeof formDetails.settings.brandedForms === "object" && {
          brandedForms: formDetails.settings.brandedForms,
        }),
      },
    },
    formJSON: {
      locale: formDetails.locale || null,
      type: formDetails.type || null,
      name: formDetails.name || null,
      shortName: formDetails.shortName || null,
      // Preserve-only — see round-trip-preservation.md. Omitted entirely when
      // absent so forms that never had them stay byte-identical.
      ...(formDetails.signatureAddendum != null && {
        signatureAddendum: formDetails.signatureAddendum,
      }),
      ...(formDetails.signatureAddendumSources != null && {
        signatureAddendumSources: formDetails.signatureAddendumSources,
      }),
      status: "new",
      startSectionAttributeName: startSectionAttributeName,
      lineNumbers: formDetails.lineNumbers || false,
      // `initialsJSON` lives at the top level (sibling to
      // `signaturesJSON`). The earlier inside-formJSON copy was a
      // duplicate that bloated every export by ~the size of the
      // initials map; importer still tolerates either location for
      // back-compat.
      formData: [
        {
          status: "draft",
          // Verbatim passthrough — authored in JSON, never built here. Omitted
          // when absent so forms without them stay byte-identical.
          ...(formDetails.copySectionsIfDecision != null && {
            copySectionsIfDecision: formDetails.copySectionsIfDecision,
          }),
          ...(formDetails.compareForNoChangeOrDeleted != null && {
            compareForNoChangeOrDeleted: formDetails.compareForNoChangeOrDeleted,
          }),
          sections: (() => {
            if (sectionsSuppressed) return [];
            const sortedSections = orderedSections.sort(
              (a, b) => a.originalIndex - b.originalIndex,
            );
            return sortedSections.map((section, sectionIndex) => {
              const { originalIndex, ...sectionWithoutIndex } = section;
              const sectionFields = fields
                .filter((field) => field.sectionId === section.id && !field.isOverflow && !field.excludeFromFormJSON)
                .map((field, idx) => buildPublishedFieldOutput(field, fields, idx + 1));

              const sectionAttributeName = (sectionWithoutIndex.attributeName ?? "").trim();
              const sectionsForNav = sortedSections.map((s) => {
                const { originalIndex: _oi, ...rest } = s;
                return rest;
              });

              return {
                fields: sectionFields,
                title: sectionWithoutIndex.title,
                attributeName: sectionAttributeName,
                type: sectionWithoutIndex.type || "data",
                titleShort: sectionWithoutIndex.titleShort || null,
                subtitle: sectionWithoutIndex.subtitle || null,
                description: sectionWithoutIndex.description || null,
                info: sectionWithoutIndex.info || null,
                metadata: reorderSectionMetadata(sectionWithoutIndex.metadata) || null,
                goTo: resolveSectionGoToForExport(
                  sectionWithoutIndex,
                  sectionsForNav,
                  sectionIndex,
                ),
              };
            });
          })(),
        },
      ],
    },
    formSummaryJSON: { ...generateSummaryJSON(fields, "formSummary"), ...extraFormSummary },
    dealSummaryJSON: { ...generateSummaryJSON(fields, "dealSummary"), ...extraDealSummary },
    signaturesJSON,
    initialsJSON,
    additionalFieldsJSON,
    subsequentSigningJSON,
    // Verbatim passthrough — see parseFormJSON. Authored in JSON, never built here.
    rulesJSON: rulesJSON && typeof rulesJSON === "object" ? rulesJSON : {},
  };

  return JSON.stringify(deepNormalizePdfRectKeyOrder(form), null, 2);
};
